import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import SupportChatModal from "./SupportChatModal";
import "./clientTheme.css";
import "./SupportButton.css";

const SUPPORT_PHONE = "+972527477128";
const WHATSAPP_PHONE = "972527477128";

function HeadsetIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5Z" />
      <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5Z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.79.47 3.47 1.29 4.92L2 22l5.29-1.39a9.87 9.87 0 0 0 4.75 1.21h.01c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm5.8 14.02c-.24.68-1.4 1.3-1.93 1.35-.5.05-1.13.07-1.83-.12-.42-.12-.96-.3-1.65-.6-2.9-1.25-4.79-4.17-4.94-4.36-.15-.2-1.18-1.57-1.18-3 0-1.42.75-2.12 1.02-2.41.27-.29.58-.36.78-.36.2 0 .39 0 .56.01.18.01.42-.07.65.5.24.58.82 2 .89 2.15.07.15.11.32.02.51-.09.2-.14.32-.28.49-.14.17-.29.38-.42.51-.14.14-.28.29-.12.57.15.28.68 1.13 1.47 1.83 1.01.9 1.86 1.19 2.14 1.32.28.14.44.12.6-.07.16-.2.68-.79.87-1.06.18-.27.36-.22.6-.13.24.09 1.55.73 1.82.86.27.14.44.2.51.32.07.11.07.65-.17 1.29Z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

export default function SupportButton() {
  const { t } = useTranslation("supportButton");
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { isAuthenticated, openLoginModal } = useAuth();

  function toggle() {
    setOpen((prev) => !prev);
  }

  function openChat() {
    setOpen(false);
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    setChatOpen(true);
  }

  function openWhatsApp() {
    setOpen(false);
    window.open(`https://wa.me/${WHATSAPP_PHONE}`, "_blank", "noopener,noreferrer");
  }

  function callSupport() {
    setOpen(false);
    window.location.href = `tel:${SUPPORT_PHONE}`;
  }

  return (
    <div className="client-support-fab">
      {open && (
        <div className="client-support-options">
          <button type="button" className="client-support-option chat" onClick={openChat}>
            <ChatIcon />
            <span>{t("chatOption")}</span>
          </button>
          <button type="button" className="client-support-option whatsapp" onClick={openWhatsApp}>
            <WhatsAppIcon />
            <span>{t("whatsappOption")}</span>
          </button>
          <button type="button" className="client-support-option call" onClick={callSupport}>
            <PhoneIcon />
            <span>{t("callOption")}</span>
          </button>
        </div>
      )}
      <button
        type="button"
        className="client-support-main"
        onClick={toggle}
        aria-label={open ? t("closeMenuLabel") : t("openMenuLabel")}
      >
        {open ? <CloseIcon /> : <HeadsetIcon />}
      </button>

      {chatOpen && <SupportChatModal onClose={() => setChatOpen(false)} />}
    </div>
  );
}
